import { useState, useRef } from 'react';
import {
  downloadInvoices,
  getDownloadStatus,
  getCloseBrowser,
  cleanupAllSessions,
} from '../services/invoice-downloader-api';
import { waitNSeconds } from '../utils/common-functions';
import { showErrorMessage, showSuccessMessage } from '../utils/toast-functions';
import { InvoiceDownloadResponse } from '../models/invoices/InvoiceDownloadApiResponse';
import {
  DownloadStatusData,
  DownloadStatusResponse,
} from '../models/invoices/DownloadStatusResponse';
import { INVOICE_DOWNLOADER_BASE_URL } from '../services/constants';

const DOWNLOAD_STATUS_CHECK_WAIT_TIME_SECONDS = 2;
const DOWNLOAD_POLL_TIMEOUT_MINUTES = 60;
const MAX_DOWNLOAD_STATUS_CHECK_ATTEMPTS =
  (DOWNLOAD_POLL_TIMEOUT_MINUTES * 60) /
  DOWNLOAD_STATUS_CHECK_WAIT_TIME_SECONDS;

const CURRENT_YEAR = new Date().getFullYear();

export const useInvoiceDownloaderDownload = (
  sessionId: string,
  onDownloadComplete: (sessionId: string) => void,
) => {
  const [invoices, setInvoices] = useState<number>(1);
  const [fromYear, setFromYear] = useState<string>(String(CURRENT_YEAR));
  const [fromMonth, setFromMonth] = useState<string>('01');
  const [downloadLink, setDownloadLink] = useState<string>('');
  const [downloadInProgress, setDownloadInProgress] = useState<boolean>(false);
  const [downloadStatus, setDownloadStatus] =
    useState<DownloadStatusData | null>(null);
  const [userSessionCounter, setUserSessionCounter] = useState<number>(0);
  const isPollingCancelledRef = useRef<boolean>(false);
  const browserClosedRef = useRef<boolean>(false);

  const closeBrowserSession = async (sessionIdToClose: string) => {
    if (!browserClosedRef.current) {
      try {
        const browserResponse = await getCloseBrowser(sessionIdToClose);
        console.log('Closing browser session:');
        console.log(browserResponse.data);
        browserClosedRef.current = true;
      } catch (err) {
        console.error('Error closing browser session:', err);
      }
    }
  };

  const pollDownloadStatus = async (sessionIdToPoll: string) => {
    console.debug('Starting download status polling...');
    let attempts = 0;
    const startTime = Date.now();
    const timeoutMs = DOWNLOAD_POLL_TIMEOUT_MINUTES * 60 * 1000;

    while (attempts < MAX_DOWNLOAD_STATUS_CHECK_ATTEMPTS) {
      if (isPollingCancelledRef.current) {
        console.debug('Download status polling was cancelled');
        setDownloadInProgress(false);
        setDownloadStatus(null);
        await closeBrowserSession(sessionIdToPoll);
        return;
      }

      if (Date.now() - startTime > timeoutMs) {
        console.debug('Download status polling timed out after 1 hour');
        showErrorMessage('Download timed out after 1 hour');
        setDownloadInProgress(false);
        setDownloadStatus(null);
        await closeBrowserSession(sessionIdToPoll);
        return;
      }

      console.debug(
        `Download status check - Attempt number ${attempts + 1} of ${MAX_DOWNLOAD_STATUS_CHECK_ATTEMPTS}.`,
      );

      try {
        const response: DownloadStatusResponse =
          await getDownloadStatus(sessionIdToPoll);

        if (response?.success && response.data) {
          const status = response.data;
          setDownloadStatus(status);

          if (status.completedInvoices >= status.totalInvoices) {
            console.debug('Download completed successfully');
            showSuccessMessage('Invoices downloaded successfully');
            setDownloadInProgress(false);
            await closeBrowserSession(sessionIdToPoll);
            onDownloadComplete(sessionIdToPoll);
            return;
          }
        } else {
          console.error('Download status check returned non-success response');
          showErrorMessage('Failed to check download status');
          setDownloadInProgress(false);
          setDownloadStatus(null);
          await closeBrowserSession(sessionIdToPoll);
          return;
        }
      } catch (err) {
        console.error('Error checking download status:', err);
        showErrorMessage('Failed to check download status');
        setDownloadInProgress(false);
        setDownloadStatus(null);
        await closeBrowserSession(sessionIdToPoll);
        return;
      }

      attempts++;

      if (attempts < MAX_DOWNLOAD_STATUS_CHECK_ATTEMPTS) {
        await waitNSeconds(DOWNLOAD_STATUS_CHECK_WAIT_TIME_SECONDS);
      }
    }

    console.debug('Download status check failed. Giving up.');
    showErrorMessage('Download status check failed after maximum attempts');
    setDownloadInProgress(false);
    setDownloadStatus(null);
    await closeBrowserSession(sessionIdToPoll);
  };

  const handleDownloadInvoices = async (
    e: React.FormEvent,
    currentSessionId: string,
  ): Promise<{ error?: string } | void> => {
    e.preventDefault();
    setDownloadInProgress(false);
    setDownloadStatus(null);
    isPollingCancelledRef.current = false;
    browserClosedRef.current = false;
    console.log('Starting invoice download...');

    try {
      const response: InvoiceDownloadResponse = await downloadInvoices(
        currentSessionId,
        invoices,
        fromYear,
        fromMonth,
      );
      if (response.success) {
        showSuccessMessage('Invoice download started');
        setDownloadInProgress(true);
        await pollDownloadStatus(currentSessionId);
      } else {
        showErrorMessage('Failed to start invoice download');
        return { error: response.error.message };
      }
    } catch (err) {
      console.error('Error starting invoice download:', err);
      showErrorMessage('Failed to start invoice download');
      return { error: 'Failed to start invoice download' };
    }
  };

  const handleCancelDownload = async (currentSessionId: string) => {
    isPollingCancelledRef.current = true;
    setDownloadInProgress(false);
    setDownloadStatus(null);

    try {
      await closeBrowserSession(currentSessionId);
      showSuccessMessage('Download cancelled');
    } catch (err) {
      console.error('Error cancelling download:', err);
      showErrorMessage('Failed to cancel download');
    }
  };

  const resetDownload = (success: boolean, sessionIdToUse: string) => {
    setDownloadInProgress(false);
    setDownloadStatus(null);
    isPollingCancelledRef.current = false;
    browserClosedRef.current = false;
    setInvoices(1);
    setFromYear(String(CURRENT_YEAR));
    setFromMonth('01');

    if (success) {
      setDownloadLink(
        `${INVOICE_DOWNLOADER_BASE_URL}/downloadZipFile?sessionId=${sessionIdToUse}`,
      );
      setUserSessionCounter((prev) => prev + 1);
    } else {
      setDownloadLink('');
    }
  };

  const handleDownloadZipFile = (e: React.FormEvent) => {
    e.preventDefault();
    const url = downloadLink;
    window.open(url, '_blank');
  };

  const handleDownloadLogsFile = (e: React.FormEvent) => {
    e.preventDefault();
    const url = downloadLink.replace('Zip', 'Log');
    window.open(url, '_blank');
  };

  const handleCleanupAllSessions = async (
    e: React.FormEvent,
  ): Promise<{ error?: string } | void> => {
    e.preventDefault();

    try {
      const response = await cleanupAllSessions();
      if (response.success) {
        showSuccessMessage('All sessions cleaned up successfully');
        setDownloadLink('');
        setUserSessionCounter(0);
      } else {
        showErrorMessage('Failed to cleanup sessions');
        return {
          error: `Failed to cleanup sessions: ${response.error.message}`,
        };
      }
    } catch (err) {
      console.error('Error cleaning up sessions:', err);
      showErrorMessage('Failed to cleanup sessions');
      return { error: 'Failed to cleanup sessions' };
    }
  };

  return {
    // State
    invoices,
    fromYear,
    fromMonth,
    downloadLink,
    downloadInProgress,
    downloadStatus,
    userSessionCounter,
    // Setters
    setInvoices,
    setFromYear,
    setFromMonth,
    // Handlers
    handleDownloadInvoices,
    handleCancelDownload,
    handleDownloadZipFile,
    handleDownloadLogsFile,
    handleCleanupAllSessions,
    resetDownload,
  };
};
